/**
 * §P.4 · Los recibos del club.
 *
 * Dos pantallas en una, y es a propósito: `?unpaid=true` **es** la vista de morosos
 * por recibo, así que separarlas obligaría a mantener dos listas que enseñan lo
 * mismo con otro filtro. Lo que sí está aparte es `/debtors`, que agrupa por familia
 * y contesta otra pregunta: «a quién llamo hoy».
 *
 * ## El lote del lunes
 *
 * Marcar doce recibos cobrados de uno en uno son doce formularios y doce esperas.
 * `bulk-payments` los marca de una vez, **cada uno por lo que le queda pendiente**:
 * son doce familias con doce cuotas distintas, y no hay un importe común que teclear.
 *
 * Y **no se rompe por una fila mala**: de doce, si uno está anulado y otro ya estaba
 * pagado, los diez restantes se cobran y el resultado dice qué pasó con los dos.
 * Abortar el lote entero obligaría a repetir la lista a mano, y esa fricción es la
 * que hace que un club vuelva al Excel — así que el resultado se enseña entero.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import { aProblema, ErrorDeApi, type Problema } from '@/shared/api/errores';
import { Cargando, Error as PanelError, Vacio } from '@/shared/ui/Estados';
import { fechaParaInput, euros, fecha } from '@/shared/ui/formato';
import {
  anterior,
  cursorActual,
  esLaPrimera,
  numeroDePagina,
  PRIMERA,
  siguiente,
  type PilaDeCursores,
} from '@/shared/ui/paginas';
import {
  etiquetaDeEstado,
  etiquetaDePeriodo,
  METODOS_A_MANO,
  pendienteDe,
  periodosOfrecidos,
  type MetodoDeCobro,
  type PaginaDeRecibos,
  type Recibo,
} from '@/club/facturacion/tipos';

interface ResultadoDelLote {
  readonly paid: readonly { invoice_id: string; amount_cents: number }[];
  readonly skipped: readonly { invoice_id: string; reason: string }[];
  readonly total_cents: number;
}

const MOTIVO_DEL_LOTE: Record<string, string> = {
  not_found: 'no se encontró',
  voided: 'está anulado',
  already_paid: 'ya estaba pagado',
};

/**
 * Un recibo se puede cobrar si le queda algo pendiente y ya está emitido.
 *
 * No hace falta excluir los anulados: `pendienteDe` ya devuelve cero para ellos.
 * Los borradores sí, y por otra razón — todavía no son un documento que nadie haya
 * recibido, así que no hay nada que cobrar.
 */
const sePuedeCobrar = (r: Recibo): boolean =>
  r.status !== 'draft' && pendienteDe(r) > 0;

export function ListaDeRecibos({
  club,
  puedeCobrar,
  periodoInicial,
  soloDeudasAlEmpezar = false,
  alAbrir,
}: {
  club: string;
  puedeCobrar: boolean;
  /** Llega desde la remesa recién lanzada: «ver los recibos de este mes». */
  periodoInicial?: string;
  /** Llega desde «Anotar un cobro» de Inicio: lo que está por cobrar. */
  soloDeudasAlEmpezar?: boolean;
  alAbrir: (reciboId: string) => void;
}) {
  const cache = useQueryClient();
  const [soloDeudas, setSoloDeudas] = useState(soloDeudasAlEmpezar);
  const [periodo, setPeriodo] = useState(periodoInicial ?? '');
  const [cursores, setCursores] = useState<PilaDeCursores>(PRIMERA);
  const cursor = cursorActual(cursores);

  const [marcados, setMarcados] = useState<ReadonlySet<string>>(new Set());
  const [cobrandoLote, setCobrandoLote] = useState(false);
  const [metodo, setMetodo] = useState<MetodoDeCobro>('transfer');
  const [referencia, setReferencia] = useState('');
  /*
   * Cuándo entró el dinero, **una fecha para todo el lote**: es lo que significa un
   * lote, que salieron del mismo extracto. Por defecto hoy.
   */
  const [cuando, setCuando] = useState(() => fechaParaInput(new Date()));
  const [problema, setProblema] = useState<Problema | null>(null);
  const [resultado, setResultado] = useState<ResultadoDelLote | null>(null);

  const filtrar = (cambio: () => void) => {
    cambio();
    // Cambiar un filtro vuelve a la primera página: el cursor de la anterior no
    // significa nada en la lista nueva.
    setCursores(PRIMERA);
    setMarcados(new Set());
  };

  const recibos = useQuery({
    queryKey: ['recibos', club, soloDeudas, periodo, cursor],
    queryFn: async () => {
      const { data, error } = await api.v1.clubs({ club }).invoices.get({
        query: {
          limit: 25,
          ...(soloDeudas ? { unpaid: true } : {}),
          ...(periodo ? { period: periodo } : {}),
          ...(cursor ? { cursor } : {}),
        },
      });
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as PaginaDeRecibos;
    },
  });

  const cobrables = (recibos.data?.data ?? []).filter(sePuedeCobrar);

  const cobrarLote = useMutation({
    mutationFn: async () => {
      const r = await api.v1.clubs({ club }).invoices['bulk-payments'].post(
        {
          invoice_ids: [...marcados],
          method: metodo,
          reference: referencia.trim() === '' ? null : referencia.trim(),
          charge_date: cuando === '' ? null : cuando,
        },
        // Clave nueva por pulsación: una guardada devolvería el resultado del lote
        // anterior y diría que se cobró lo que no se cobró.
        { headers: { 'idempotency-key': crypto.randomUUID() } }
      );
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data as unknown as ResultadoDelLote;
    },
    onSuccess: (res) => {
      setResultado(res);
      setCobrandoLote(false);
      setMarcados(new Set());
      setReferencia('');
      void cache.invalidateQueries({ queryKey: ['recibos', club] });
      void cache.invalidateQueries({ queryKey: ['morosos', club] });
    },
    onError: (e) => setProblema(aProblema(e)),
  });

  const alternar = (id: string) =>
    setMarcados((m) => {
      const nuevo = new Set(m);
      if (nuevo.has(id)) nuevo.delete(id);
      else nuevo.add(id);
      return nuevo;
    });

  const pendienteMarcado = cobrables
    .filter((r) => marcados.has(r.id))
    .reduce((s, r) => s + pendienteDe(r), 0);

  // ── El formulario del lote ───────────────────────────────────────────────
  if (cobrandoLote) {
    return (
      <form
        className="formulario"
        onSubmit={(e) => {
          e.preventDefault();
          cobrarLote.mutate();
        }}
      >
        <h1>
          Marcar {marcados.size === 1 ? '1 recibo' : `${marcados.size} recibos`}{' '}
          como cobrados
        </h1>

        <p className="tenue">
          Cada uno por <strong>lo que le queda pendiente</strong>, que suman{' '}
          {euros(pendienteMarcado)}. No hay que teclear ningún importe: son
          familias con cuotas distintas.
        </p>

        <div className="rejilla">
          <label>
            Cómo han pagado
            <select
              value={metodo}
              onChange={(e) => setMetodo(e.target.value as MetodoDeCobro)}
            >
              {METODOS_A_MANO.map((m) => (
                <option key={m.valor} value={m.valor}>
                  {m.etiqueta}
                </option>
              ))}
            </select>
          </label>

          <label>
            Cuándo entró
            {/*
              La fecha del extracto, no la del lunes que se teclea. Una para los
              doce: es lo que significa que sean un lote.
            */}
            <input
              type="date"
              value={cuando}
              onChange={(e) => setCuando(e.target.value)}
            />
          </label>

          <label>
            Referencia
            <input
              maxLength={200}
              placeholder={
                METODOS_A_MANO.find((m) => m.valor === metodo)?.pista ??
                'Opcional'
              }
              value={referencia}
              onChange={(e) => setReferencia(e.target.value)}
            />
          </label>
        </div>

        <div className="nota">
          La fecha y la referencia son{' '}
          <strong>lo que permite cuadrar con el extracto</strong> dentro de tres
          meses. Las dos son las mismas para todo el lote, así que sirven para
          «la transferencia del 5», no para el concepto de cada Bizum.
        </div>

        {problema && <PanelError problema={problema} />}

        <div className="acciones">
          <button type="submit" disabled={cobrarLote.isPending}>
            {cobrarLote.isPending ? 'Marcando…' : 'Marcarlos cobrados'}
          </button>
          <button
            type="button"
            className="btn-plano"
            disabled={cobrarLote.isPending}
            onClick={() => setCobrandoLote(false)}
          >
            Cancelar
          </button>
        </div>
      </form>
    );
  }

  // ── La lista ─────────────────────────────────────────────────────────────
  return (
    <section>
      <header className="titulo-fila">
        <h1>Recibos</h1>
      </header>

      <p className="tenue">
        Lo emitido, lo cobrado y lo que falta. Un recibo{' '}
        <strong>no se borra nunca</strong>: se anula, y sigue aquí.
      </p>

      {/*
        El resultado del lote, entero: lo cobrado y lo que se saltó con su motivo.
        Esconder los saltados es lo que hace que alguien dé por cobrado lo que no.
      */}
      {resultado && (
        <div className="aviso aviso-calma" role="status">
          <strong>
            {resultado.paid.length === 1
              ? 'Se ha marcado 1 recibo'
              : `Se han marcado ${resultado.paid.length} recibos`}{' '}
            por {euros(resultado.total_cents)}.
          </strong>
          {resultado.skipped.length > 0 && (
            <ul className="lista-clara">
              {resultado.skipped.map((s) => (
                <li key={s.invoice_id}>
                  Uno se quedó sin marcar porque{' '}
                  {MOTIVO_DEL_LOTE[s.reason] ?? s.reason}.
                </li>
              ))}
            </ul>
          )}
          <div className="hueco-acciones">
            <button
              type="button"
              className="btn-plano"
              onClick={() => setResultado(null)}
            >
              Entendido
            </button>
          </div>
        </div>
      )}

      <div className="filtros">
        <label className="opcion">
          <input
            type="checkbox"
            checked={soloDeudas}
            onChange={(e) => filtrar(() => setSoloDeudas(e.target.checked))}
          />
          Solo lo que se debe
        </label>

        <label>
          Mes
          <select
            value={periodo}
            onChange={(e) => filtrar(() => setPeriodo(e.target.value))}
          >
            <option value="">Todos</option>
            {periodosOfrecidos(new Date(), 12).map((p) => (
              <option key={p} value={p}>
                {etiquetaDePeriodo(p)}
              </option>
            ))}
          </select>
        </label>
      </div>

      {puedeCobrar && marcados.size > 0 && (
        <div className="aviso" role="status">
          <strong>
            {marcados.size === 1
              ? '1 recibo seleccionado'
              : `${marcados.size} recibos seleccionados`}
            , {euros(pendienteMarcado)} pendientes.
          </strong>
          <div className="hueco-acciones">
            <button type="button" onClick={() => setCobrandoLote(true)}>
              Marcarlos cobrados
            </button>
            <button
              type="button"
              className="btn-plano"
              onClick={() => setMarcados(new Set())}
            >
              Quitar la selección
            </button>
          </div>
        </div>
      )}

      {recibos.isPending && <Cargando filas={6} etiqueta="Cargando recibos" />}

      {recibos.isError && (
        <PanelError
          problema={aProblema(recibos.error)}
          reintentar={() => void recibos.refetch()}
        />
      )}

      {recibos.data?.data.length === 0 && (
        <Vacio
          titulo={
            soloDeudas
              ? 'No se debe nada'
              : periodo
                ? 'No hay recibos de ese mes'
                : 'Todavía no hay recibos'
          }
          explicacion={
            soloDeudas
              ? 'Todo lo emitido está cobrado.'
              : 'Los recibos salen de la remesa del mes, que emite uno por familia con cuota activa.'
          }
        />
      )}

      {recibos.data && recibos.data.data.length > 0 && (
        <>
          {/* En el móvil, una tarjeta por recibo (`tabla-tarjetas`, tema.css). */}
          <div className="tabla-marco">
            <table className="tabla tabla-tarjetas">
              <thead>
                <tr>
                  {puedeCobrar && <th />}
                  <th>Paga</th>
                  <th>Mes</th>
                  <th>Nº</th>
                  <th className="num">Total</th>
                  <th className="num">Pendiente</th>
                  <th>Estado</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {recibos.data.data.map((r) => {
                  const pendiente = pendienteDe(r);
                  return (
                    <tr
                      key={r.id}
                      className={r.status === 'void' ? 'fila-fuera' : undefined}
                    >
                      {puedeCobrar && (
                        <td data-label="Para cobrar">
                          {/* Un anulado o uno ya pagado no se puede marcar: no hay casilla. */}
                          {sePuedeCobrar(r) && (
                            <input
                              type="checkbox"
                              aria-label={`Seleccionar el recibo de ${r.payer_name}`}
                              checked={marcados.has(r.id)}
                              onChange={() => alternar(r.id)}
                            />
                          )}
                        </td>
                      )}
                      <td className="celda-titulo">
                        <button
                          type="button"
                          className="enlace-fila"
                          onClick={() => alAbrir(r.id)}
                        >
                          {r.payer_name}
                        </button>
                      </td>
                      <td className="tenue" data-label="Mes">
                        {etiquetaDePeriodo(r.period)}
                      </td>
                      <td className="tenue" data-label="Nº">
                        {r.number ?? '—'}
                      </td>
                      <td className="num" data-label="Total">
                        {euros(r.total_cents)}
                      </td>
                      <td className="num" data-label="Pendiente">
                        {/* Un parcial se ve: «quedan 14,50 €» no es lo mismo que «debe 29 €». */}
                        {pendiente <= 0 ? (
                          <span className="tenue">—</span>
                        ) : (
                          <strong>{euros(pendiente)}</strong>
                        )}
                      </td>
                      <td data-label="Estado">
                        <EstadoDeRecibo recibo={r} />
                      </td>
                      <td className="tenue">
                        {r.due_date ? `Vence ${fecha(r.due_date)}` : ''}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Por cursor, nunca por número de página. Ver `shared/ui/paginas`. */}
          <div className="paginacion">
            <button
              type="button"
              className="btn-plano"
              disabled={esLaPrimera(cursores) || recibos.isFetching}
              onClick={() => setCursores(anterior)}
            >
              Anterior
            </button>
            <span className="tenue">
              {esLaPrimera(cursores)
                ? 'Primera página'
                : `Página ${numeroDePagina(cursores)}`}
            </span>
            <button
              type="button"
              className="btn-plano"
              disabled={!recibos.data.has_more || recibos.isFetching}
              onClick={() =>
                setCursores((c) =>
                  siguiente(c, recibos.data?.next_cursor ?? null)
                )
              }
            >
              Siguiente
            </button>
          </div>
        </>
      )}
    </section>
  );
}

/**
 * El estado, con el matiz que la columna `status` no da.
 *
 * **Un cobro parcial no marca el recibo como pagado**: sigue siendo `issued` y sigue
 * debiendo algo. Pintarlo solo como «Emitido» esconde que ya entró dinero, y es la
 * diferencia entre volver a reclamar el total o reclamar lo que falta.
 */
export function EstadoDeRecibo({ recibo }: { recibo: Recibo }) {
  const parcial =
    recibo.paid_cents > 0 && recibo.paid_cents < recibo.total_cents;

  const clase =
    recibo.status === 'paid'
      ? 'pastilla-bien'
      : recibo.status === 'void'
        ? 'pastilla'
        : recibo.status === 'failed'
          ? 'pastilla-mal'
          : 'pastilla-info';

  return (
    <span className={`pastilla ${clase}`}>
      {parcial ? 'Pagado a medias' : etiquetaDeEstado(recibo.status)}
    </span>
  );
}
